import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import { NotFoundPage } from "./pages/NotFound";
import { VariationApp } from "./variations/VariationApp";

/**
 * Code-splitting strategy:
 *  - Funnel route (`/:slug`) ships in the main bundle — every paid
 *    visitor lands here, optimise for fastest first paint.
 *  - **Success page** is lazy: it pulls the Firebase Web SDK
 *    (~60 KB gz), only loads after a successful Paddle checkout.
 *  - **Admin** (including the dev landing/preview screen) is lazy
 *    and gated by Cloudflare Zero Trust at the edge — real users
 *    never request it.
 *
 * Routing surface:
 *  - `/`             → 404 (the public root is deliberately unmapped;
 *                     real users land on `/:slug` from marketing URLs).
 *  - `/:slug`        → the variation funnel.
 *  - `/:slug/success` → the post-checkout success page.
 *  - `/admin`        → CF-Access-gated dashboard.
 *  - `/admin/preview`→ the dev landing page (variation list + Success
 *                     preview deeplinks), reachable only behind Access.
 */
const SuccessPage = lazy(() =>
  import("./pages/Success").then((m) => ({ default: m.SuccessPage }))
);
// Lazy-loaded — `LandingPage` is no longer in the public bundle since
// it's now an admin-only preview screen.
const LandingPage = lazy(() =>
  import("./pages/Landing").then((m) => ({ default: m.LandingPage }))
);
const AdminLayout = lazy(() =>
  import("./admin/AdminLayout").then((m) => ({ default: m.AdminLayout }))
);
const DashboardPage = lazy(() =>
  import("./admin/Dashboard").then((m) => ({ default: m.DashboardPage }))
);
const VariationDetailPage = lazy(() =>
  import("./admin/VariationDetail").then((m) => ({
    default: m.VariationDetailPage,
  }))
);

function PageFallback({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="min-h-[100dvh] bg-bg-canvas grid place-items-center">
      <div className="text-fg-secondary text-sm animate-pulse">{label}</div>
    </div>
  );
}

export const routes: RouteObject[] = [
  // Each variation is its own mini-app rooted at /:slug. Variation-
  // specific components live in apps/web/src/variations/<slug>/.
  { path: "/:slug", element: <VariationApp /> },
  {
    path: "/:slug/success",
    element: (
      <Suspense fallback={<PageFallback label="Setting up…" />}>
        <SuccessPage />
      </Suspense>
    ),
  },
  {
    path: "/admin",
    element: (
      <Suspense fallback={<PageFallback />}>
        <AdminLayout />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageFallback />}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        // Admin-only "preview" screen: lists every active variation +
        // exposes Success-page deeplinks for QA. Lives under /admin so
        // CF Access gates it; users never see this page.
        path: "preview",
        element: (
          <Suspense fallback={<PageFallback />}>
            <LandingPage />
          </Suspense>
        ),
      },
      {
        path: "variation/:slug",
        element: (
          <Suspense fallback={<PageFallback />}>
            <VariationDetailPage />
          </Suspense>
        ),
      },
    ],
  },
  // Public root + any unknown path → 404. Real users come in through
  // /:slug marketing URLs; the bare root is no longer a discoverable
  // surface.
  { path: "*", element: <NotFoundPage /> },
];
